import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';

import { useSearchPersonQuery, useLazySearchPersonIdQuery } from '../actions/tmdbApiSlice';
import PosterCard from '../components/PosterCard';

const placeholderImageUrl = 'https://placehold.co/300x450.png?text=No+Poster';

const PeopleScreen = () => {
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q');
  const isDirector = searchParams.has('dir');

  const { data, error, isFetching, isSuccess } = useSearchPersonQuery(searchQuery);
  const [triggerSearch, { data: pidData }] = useLazySearchPersonIdQuery();
  const navigate = useNavigate();

  useEffect(() => {
    if (isSuccess && data?.results && data.results.length > 0) {
      triggerSearch(data.results[0].id);
    }
  }, [isSuccess, data, triggerSearch]);

  const handlePersonItemNavigate = (item) => {
    let year = '';
    if (item.media_type === 'movie' && item.release_date) {
      year = item.release_date.split('-')[0];
    } else if (item.media_type === 'tv' && item.first_air_date) {
      year = item.first_air_date.split('-')[0];
    }
    const idParam = item.media_type === 'tv' ? 'tid' : 'mid';
    const titleWithYear = year ? `${item.title || item.name} ${year}` : item.title || item.name;
    navigate(`/details/?q=${titleWithYear}&${idParam}=${item.id}`);
  };

  if (isFetching) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return <Container sx={{ py: 6 }}><Typography color="error">Error: {error.message}</Typography></Container>;
  }

  const personList = data?.results;
  const person = personList && personList.length > 0 ? personList[0] : null;

  const sortedCast = pidData?.cast?.slice().sort((a, b) => {
    const dateA = a.release_date || a.first_air_date;
    const dateB = b.release_date || b.first_air_date;
    if (!dateA) return 1;
    if (!dateB) return -1;
    return new Date(dateB) - new Date(dateA);
  });

  const sortedCrew = pidData?.crew?.slice().sort((a, b) => {
    const aIsDir = a.job === 'Director';
    const bIsDir = b.job === 'Director';
    if (aIsDir && !bIsDir) return -1;
    if (!aIsDir && bIsDir) return 1;
    const dateA = a.release_date || a.first_air_date;
    const dateB = b.release_date || b.first_air_date;
    if (!dateA) return 1;
    if (!dateB) return -1;
    return new Date(dateB) - new Date(dateA);
  });

  const itemsToRender = isDirector ? sortedCrew : sortedCast;

  return (
    <>
      <Helmet>
        <title>{person ? `${person.name} | AirCheck` : 'AirCheck'}</title>
      </Helmet>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {person && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4, flexWrap: 'wrap' }}>
            <Avatar
              src={person.profile_path ? `https://image.tmdb.org/t/p/w300${person.profile_path}` : undefined}
              alt={person.name}
              sx={{
                width: 140,
                height: 140,
                border: (t) => `3px solid ${t.palette.divider}`,
              }}
            />
            <Box>
              <Typography variant="overline" color="text.secondary">
                {isDirector ? 'Director' : 'Actor'}
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                {person.name}
              </Typography>
              {person.known_for_department && (
                <Typography color="text.secondary">
                  {person.known_for_department}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        <Divider sx={{ mb: 3 }} />

        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          Known for
        </Typography>

        <Grid container spacing={2}>
          {itemsToRender && itemsToRender.map((item) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={`${item.credit_id || item.id}-${item.character || item.job}`}>
              <PosterCard
                src={item.poster_path ? `https://image.tmdb.org/t/p/w342${item.poster_path}` : placeholderImageUrl}
                title={item.title || item.name}
                subtitle={
                  <>
                    {item.job || item.character ? `as ${item.job || item.character}` : ''}
                    {(item.release_date || item.first_air_date) ? ` · ${item.release_date || item.first_air_date}` : ''}
                  </>
                }
                badge={
                  item.vote_average
                    ? `★ ${(Math.floor(item.vote_average * 10) / 10).toFixed(1)}`
                    : undefined
                }
                onClick={() => handlePersonItemNavigate(item)}
                width="100%"
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    </>
  );
};

export default PeopleScreen;
